import { useState, useEffect, useRef } from 'react'
import { VitalSignsChart } from './VitalSignsChart'
import { GrowthCentileChart } from './GrowthCentileChart'
import { UnitPreference } from '../types/vitalSigns'
import { Patient } from '../types/patient'
import { getUnitPreference } from '../utils/userPreferences'
import { calculateAgeInMonths } from '../utils/normativeData'
import './VitalSignsChartsDialog.css'

interface VitalSignsChartsDialogProps {
  patientId: string
  patient?: Patient
  userPreference?: UnitPreference
  isOpen: boolean
  onClose: () => void
}

export function VitalSignsChartsDialog({
  patientId,
  patient,
  userPreference,
  isOpen,
  onClose,
}: VitalSignsChartsDialogProps) {
  const actualPreference = userPreference || getUnitPreference()
  const [position, setPosition] = useState({ x: window.innerWidth - 800, y: 50 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const widgetRef = useRef<HTMLDivElement>(null)

  // Handle drag
  useEffect(() => {
    if (!isDragging || !widgetRef.current) return

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - dragStart.x
      const deltaY = e.clientY - dragStart.y
      const widgetWidth = 800
      const widgetHeight = window.innerHeight * 0.9
      const maxY = Math.max(0, window.innerHeight - widgetHeight)
      setPosition((prev) => ({
        x: Math.max(0, Math.min(window.innerWidth - widgetWidth, prev.x + deltaX)),
        y: Math.max(0, Math.min(maxY, prev.y + deltaY)),
      }))
      setDragStart({ x: e.clientX, y: e.clientY })
    }

    const handleMouseUp = () => {
      setIsDragging(false)
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging, dragStart])

  const handleDragStart = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('.vital-signs-charts-dialog-header')) {
      setIsDragging(true)
      setDragStart({ x: e.clientX, y: e.clientY })
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div
      ref={widgetRef}
      className="vital-signs-charts-dialog"
      style={{
        position: 'fixed',
        right: `${window.innerWidth - position.x - 800}px`,
        top: `${position.y}px`,
        zIndex: 1001,
      }}
      onKeyDown={handleKeyDown}
    >
      <div className="vital-signs-charts-dialog-expanded">
        <div
          className="vital-signs-charts-dialog-header"
          onMouseDown={handleDragStart}
          style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        >
          <h3>Vital Signs Charts</h3>
          <button
            className="vital-signs-charts-dialog-close"
            onClick={onClose}
            aria-label="Close"
            type="button"
          >
            ×
          </button>
        </div>
        <div className="vital-signs-charts-dialog-body">
          <div className="vital-signs-charts-grid">
            <VitalSignsChart
              patientId={patientId}
              vitalType="bloodPressure"
              userPreference={actualPreference}
              height={280}
            />
            <VitalSignsChart
              patientId={patientId}
              vitalType="heartRate"
              userPreference={actualPreference}
              height={280}
            />
            <VitalSignsChart
              patientId={patientId}
              vitalType="temperature"
              userPreference={actualPreference}
              height={280}
            />
            <VitalSignsChart
              patientId={patientId}
              vitalType="respiratoryRate"
              userPreference={actualPreference}
              height={280}
            />
            {patient && (patient.gender === 'male' || patient.gender === 'female') && (
              <>
                <VitalSignsChart
                  patientId={patientId}
                  vitalType="weight"
                  userPreference={actualPreference}
                  height={280}
                />
                <VitalSignsChart
                  patientId={patientId}
                  vitalType="height"
                  userPreference={actualPreference}
                  height={280}
                />
                <VitalSignsChart
                  patientId={patientId}
                  vitalType="bmi"
                  userPreference={actualPreference}
                  height={280}
                />
              </>
            )}
          </div>
          {patient &&
            patient.dateOfBirth &&
            (patient.gender === 'male' || patient.gender === 'female') &&
            calculateAgeInMonths(patient.dateOfBirth) <= 60 && (
              <div className="vital-signs-charts-growth-section">
                <h4>Growth Charts (UK-WHO / WHO 2006)</h4>
                <div className="vital-signs-charts-growth-grid">
                  <GrowthCentileChart
                    patientId={patientId}
                    patient={patient}
                    chartType="weight"
                    height={280}
                  />
                  <GrowthCentileChart
                    patientId={patientId}
                    patient={patient}
                    chartType="height"
                    height={280}
                  />
                  <GrowthCentileChart
                    patientId={patientId}
                    patient={patient}
                    chartType="bmi"
                    height={280}
                  />
                  <GrowthCentileChart
                    patientId={patientId}
                    patient={patient}
                    chartType="headCircumference"
                    height={280}
                  />
                </div>
              </div>
            )}
        </div>
      </div>
    </div>
  )
}
